// Cash Flow Report Hook
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/authStore';
import type { DateRange, CashFlowReport, CashFlowMetrics, CashFlowByMethod } from '@/types/report';
import { forecastCashFlow } from '../utils/calculations';

export function useCashFlowReport(period: DateRange) {
  const { company } = useAuthStore();

  return useQuery({
    queryKey: ['cashflow-report', company?.id, period.start.toISOString(), period.end.toISOString()],
    queryFn: async (): Promise<CashFlowReport> => {
      if (!company?.id) throw new Error('No company selected');

      // Fetch all transactions for the period
      const { data: transactions, error } = await supabase
        .from('treasury_transactions')
        .select('*')
        .eq('company_id', company.id)
        .gte('date', period.start.toISOString().split('T')[0])
        .lte('date', period.end.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (error) throw error;

      // Get opening balance (sum of all transactions before period start)
      const { data: previousTransactions } = await supabase
        .from('treasury_transactions')
        .select('type, amount')
        .eq('company_id', company.id)
        .lt('date', period.start.toISOString().split('T')[0]);

      const openingBalance = previousTransactions?.reduce((balance, tx) => {
        return tx.type === 'income' 
          ? balance + (tx.amount || 0)
          : balance - (tx.amount || 0);
      }, 0) || 0;

      // Calculate metrics
      const totalIncome = transactions?.filter(tx => tx.type === 'income')
        .reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0;
      
      const totalExpenses = transactions?.filter(tx => tx.type === 'expense')
        .reduce((sum, tx) => sum + (tx.amount || 0), 0) || 0;
      
      const netCashFlow = totalIncome - totalExpenses;
      const closingBalance = openingBalance + netCashFlow;

      const metrics: CashFlowMetrics = {
        totalIncome: Math.round(totalIncome * 100) / 100,
        totalExpenses: Math.round(totalExpenses * 100) / 100,
        netCashFlow: Math.round(netCashFlow * 100) / 100,
        openingBalance: Math.round(openingBalance * 100) / 100,
        closingBalance: Math.round(closingBalance * 100) / 100,
      };

      // Group by payment method
      const methodMap = new Map<string, { income: number; expense: number }>();
      
      transactions?.forEach(tx => {
        const method = tx.payment_method || 'Non spécifié';
        const existing = methodMap.get(method);
        
        if (existing) {
          if (tx.type === 'income') {
            existing.income += tx.amount || 0;
          } else {
            existing.expense += tx.amount || 0;
          }
        } else {
          methodMap.set(method, {
            income: tx.type === 'income' ? (tx.amount || 0) : 0,
            expense: tx.type === 'expense' ? (tx.amount || 0) : 0,
          });
        }
      });

      const byMethod: CashFlowByMethod[] = Array.from(methodMap.entries())
        .map(([method, data]) => ({
          method,
          income: Math.round(data.income * 100) / 100,
          expense: Math.round(data.expense * 100) / 100,
          net: Math.round((data.income - data.expense) * 100) / 100,
        }))
        .sort((a, b) => b.net - a.net);

      // Create trend data with running balance
      let runningBalance = openingBalance;
      const trendMap = new Map<string, { income: number; expense: number; balance: number }>();
      
      transactions?.forEach(tx => {
        const dateKey = tx.date;
        const existing = trendMap.get(dateKey);
        const amount = tx.amount || 0;
        
        if (tx.type === 'income') {
          runningBalance += amount;
        } else {
          runningBalance -= amount;
        }
        
        if (existing) {
          if (tx.type === 'income') {
            existing.income += amount;
          } else {
            existing.expense += amount;
          }
          existing.balance = runningBalance;
        } else {
          trendMap.set(dateKey, {
            income: tx.type === 'income' ? amount : 0,
            expense: tx.type === 'expense' ? amount : 0,
            balance: runningBalance,
          });
        }
      });

      const trend = Array.from(trendMap.entries())
        .map(([date, data]) => ({
          date,
          income: Math.round(data.income * 100) / 100,
          expense: Math.round(data.expense * 100) / 100,
          balance: Math.round(data.balance * 100) / 100,
        }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // Calculate forecasts
      const historicalData = trend.map(t => ({
        date: new Date(t.date),
        amount: t.balance,
      }));

      const forecast = {
        days7: Math.round(forecastCashFlow(historicalData, 7) * 100) / 100,
        days30: Math.round(forecastCashFlow(historicalData, 30) * 100) / 100,
        days90: Math.round(forecastCashFlow(historicalData, 90) * 100) / 100,
      };

      return {
        period,
        metrics,
        byMethod,
        trend,
        forecast,
      };
    },
    enabled: !!company?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
